import { reportSentryError } from 'lib/sentry'
import { CustomerAgeStatus } from 'modules/customer'
import { CalendarAvailbility } from 'modules/delivery'
import { Price } from 'shared/types/price'
import { isClient } from 'shared/utils/common'
import { removeEmptySpacesFromCurrency } from 'shared/utils/currency'
import { replaceDateSnakeCaseToDash } from 'shared/utils/date'
import { isNotNil,isNotNilOrEmpty } from 'shared/utils/ramda'

import {
  Pricing,
  ProductAvailabilityPlan,
  ProductTypes,
  SpecialOfferPricing,
  TagsTypes
} from '../types/products'

export const isNewProduct = (tags: TagsTypes[]) => {
  const newProduct = tags?.filter((tag: TagsTypes) => tag.tag.name === 'Nowość')

  return isNotNilOrEmpty(newProduct)
}

export const getProductUnitDetails = (product: ProductTypes): string => {
  const { capacity, weightNet, mainUnit, displayUnit } = product
  const type: string | null = mainUnit?.type || displayUnit?.type || null

  switch (type) {
    case 'volumetric':
      return `${String(capacity)} ml`
    case 'quantitative':
      return 'szt'
    case 'weight':
      return `${String(weightNet)} g`

    default:
      return `` // empty if we have no information about the measure unit
  }
}

export const getProductPackDetails = (product: ProductTypes): string => {
  const { capacity, weightNet, weightGross, mainUnit } = product
  const type: string | null = mainUnit?.type

  switch (type) {
    case 'volumetric':
      return isNotNilOrEmpty(capacity) ? `Objętość: ${String(capacity)} ml` : ''
    case 'weight':
      return isNotNilOrEmpty(weightGross)
        ? isNotNilOrEmpty(weightNet)
          ? `Masa brutto: ${String(weightGross)} g<br />Masa netto: ${String(
              weightNet
            )} g`
          : `Masa brutto: ${String(weightGross)} g`
        : isNotNilOrEmpty(weightNet)
          ? `Masa netto: ${String(weightNet)} g`
          : ''
    default:
      return ``
  }
}

export const getPricePerUnit = (product: ProductTypes): string => {
  const { pricePerUnit, displayUnit, mainUnit } = product

  const price = pricePerUnit?.priceGross?.formatted || ''
  const unit = displayUnit?.mainUnit || mainUnit?.name

  return price && unit ? `${price}/${unit}` : ''
}

export const getMaxQuantityForSpecialOffer = (
  prod: ProductTypes
): number | null => {
  const specialOfferTotalQuantityLimit = prod.specialOfferTotalQuantityLimit
  const specialOfferMaxQuantityPerSaleOrder =
    prod.specialOfferMaxQuantityPerSaleOrder

  if (isNotNil(specialOfferTotalQuantityLimit)) {
    if (isNotNil(specialOfferMaxQuantityPerSaleOrder)) {
      return Math.min(
        specialOfferTotalQuantityLimit || 0,
        specialOfferMaxQuantityPerSaleOrder || 0
      )
    } else {
      return specialOfferTotalQuantityLimit
    }
  } else {
    if (isNotNil(specialOfferMaxQuantityPerSaleOrder)) {
      return specialOfferMaxQuantityPerSaleOrder
    } else {
      return null
    }
  }
}

export const getProperProductForm = (quantity: number) => {
  let form = ''

  if (quantity === 1) {
    form = 'produkt'
  } else if (
    quantity % 10 >= 2 &&
    quantity % 10 <= 4 &&
    (quantity % 100 < 10 || quantity % 100 >= 20)
  ) {
    form = 'produkty'
  } else {
    form = 'produktów'
  }

  return `${quantity} ${form}`
}

export const getProductMetaTitle = (product: ProductTypes) => {
  const unitDetails = getProductUnitDetails(product)
  const { metaTitle, nameDescriptionShort, nameLong, name, nameShort } = product

  if (metaTitle) {
    return metaTitle
  }

  let genericMetaTitle = ''

  if (nameDescriptionShort) {
    genericMetaTitle += `${nameDescriptionShort}, `
  }

  if (nameShort) {
    genericMetaTitle += `${nameShort}, `
  }

  if (genericMetaTitle.length > 0) {
    return `${genericMetaTitle}, ${unitDetails} | Lokalny Rolnik`
  } else {
    return `${nameLong || name} | Lokalny Rolnik`
  }
}

export const pricePerGrammar = (
  pricePerUnit: Pricing,
  unitPerPrice: string
) => {
  const price = pricePerUnit?.priceGross?.formatted || ''

  //@sieradz - if some information is missing for display return empty string
  if (!price || !unitPerPrice) {
    return ''
  }

  return `${price}/${unitPerPrice}`
}

export const formatPricePerUnit = (
  pricePerUnit: Pricing,
  unitPerPrice: string
): string => {
  if (!pricePerUnit) return ''

  try {
    return `• ${removeEmptySpacesFromCurrency(
      pricePerGrammar(pricePerUnit, unitPerPrice)
    )}`
  } catch (e) {
    reportSentryError(e, {
      context: 'Error formatting price per unit',
      extra: { pricePerUnit, unitPerPrice }
    })

    return ''
  }
}

export const avabilityProductCheck = (product: ProductTypes) => {
  const maxQuantityPerOrder = Math.max(
    product?.maxQuantityPerSaleOrder || 999999,
    0
  )

  const pickingMethod = product.pickingMethod?.toLowerCase() || ''

  const quantityLimit = ['pml', 'pbl'].includes(pickingMethod)
    ? maxQuantityPerOrder
    : Math.min(product.availableWarehouseQuantity, maxQuantityPerOrder)

  const isAvailable = product.isAvailable && quantityLimit > 0

  return { isAvailable, quantityLimit }
}

interface GetProductPricesProps {
  specialOfferPricing?: Pick<SpecialOfferPricing, 'priceGross'>
  pricing?: Pick<Pricing, 'priceGross'>
  lowestPrice?: Pick<Price, 'formatted' | 'value'>
}

export const getProductPrices = (product: GetProductPricesProps) => {
  const promoPrice = product.specialOfferPricing?.priceGross?.formatted || null
  const promoPriceNumber =
    product.specialOfferPricing?.priceGross?.value || null

  const defaultPrice = product.pricing?.priceGross?.formatted || '---'
  const defaultPriceNumber = product.pricing?.priceGross?.value || null

  const currentPrice = promoPrice || defaultPrice
  const currentPriceNumber = promoPriceNumber || defaultPriceNumber

  const lowestPrice30days = product.lowestPrice?.formatted || null
  const lowestPrice30daysNumber = product.lowestPrice?.value || null

  return {
    currentPrice,
    currentPriceNumber,
    defaultPrice,
    defaultPriceNumber,
    promoPrice,
    promoPriceNumber,
    lowestPrice30days,
    lowestPrice30daysNumber
  }
}

export const buildProductIdsQueryString = (productIds: string[]): string => {
  return productIds.map((id, i) => `product_ids[${i}]=${id}`).join('&')
}

const metaKeysEntity = [
  'product:retailer_part_no',
  'product:price',
  'product:sale_price',
  'product:orginal_price',
  'product:currency',
  'product:availability',
  'product:brand',
  'product:condition',
  'product:category',
  'category:name',
  'brand:name'
]

type EntityMetaData = Record<string, string>

const getType = () => {
  return document.querySelector('meta[name="og:type"]')
}

export const getCustomMetaData = (): EntityMetaData => {
  if (!isClient()) return {}

  const typeMeta = getType()

  const type = typeMeta?.getAttribute('content')

  // Quick check: if og:type already exists, use it immediately
  const metaTags =
    type === 'product'
      ? document.querySelectorAll('meta[name^="product:"]')
      : type === 'brand'
        ? document.querySelectorAll('meta[name^="brand:"]')
        : document.querySelectorAll('meta[name^="category:"]')

  const result: EntityMetaData = {
    'og:type': type || 'website'
  }

  metaTags.forEach(meta => {
    const name = meta.getAttribute('name')

    const content = meta.getAttribute('content')

    if (name && metaKeysEntity.includes(name)) {
      result[name] = content || ''
    }
  })

  return result
}

const UNAVAILABLE_PRODUCTS_SHORT_NAMES = {
  'lokalny rolnik': true
} as const

export const isUnavailableProduct = (
  productNameShort: string | undefined
): boolean => {
  return !!UNAVAILABLE_PRODUCTS_SHORT_NAMES[
    productNameShort?.toLowerCase() ?? ''
  ]
}

interface IsProductAvailableByAgeProps {
  product: Pick<ProductTypes, 'ageLimitMet' | 'ageLimit'>
  ageLimitMetCategory: boolean | undefined
  userAgeStatus: CustomerAgeStatus
}

export const isProductBlockedByCustomerAge = ({
  product,
  ageLimitMetCategory,
  userAgeStatus
}: IsProductAvailableByAgeProps) => {
  if (userAgeStatus === CustomerAgeStatus.adult) return false

  if (product.ageLimitMet !== undefined) return !product.ageLimitMet
  if (ageLimitMetCategory !== undefined) return !ageLimitMetCategory

  return !!product.ageLimit
}

export const avalibleForDeliveryDay = (
  substitute: ProductTypes,
  deliveryDayAsNumber: number | null
) => {
  // @sieradz - if null then there is no delivery calendar - available on any day
  if (substitute.weeklyAvailabilityPlan === null) {
    return true
    // @sieradz - check the availability of the replacement on the selected delivery day
  } else if (
    deliveryDayAsNumber &&
    !substitute.weeklyAvailabilityPlan[deliveryDayAsNumber - 1]
  ) {
    return false
  }

  // @sieradz - other cases - display replacement
  return true
}

export const mapProductAvailabilityPlantoCalendarAvailbility = (
  calendar: ProductAvailabilityPlan
): CalendarAvailbility[] => {
  if (!calendar) return []

  return Object.entries(calendar).map(([date, value]) => {
    const dateString = replaceDateSnakeCaseToDash(String(date))

    return {
      date: new Date(dateString),
      value
    }
  })
}
